"use client";

import {
  SetDefaultContractType,
  SetDefaultCurrency,
} from "@/app/actions/auth/preferences";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/hooks/use-session";
import { ContractType, Currency } from "@/types/models";
import { useActionState, useEffect, useState } from "react";

export default function Settings() {
  const { user, updateUser } = useSession();

  const [currency, setCurrency] = useState<Currency | undefined>();
  const [contractType, setContractType] = useState<ContractType | undefined>();

  useEffect(() => {
    if (user) {
      console.log(user);
      if (user?.user_metadata?.preferences?.currency) {
        setCurrency(user?.user_metadata?.preferences?.currency);
      }
      if (user?.user_metadata?.preferences?.contract_type) {
        setContractType(user?.user_metadata?.preferences?.contract_type);
      }
    }
  }, [user]);

  const [currencyError, currencySubmitAction, currencyIsPending] =
    useActionState(async (_previousState: any, formData: FormData) => {
      const curr = formData.get("currency")?.valueOf() as Currency;
      console.log(formData);
      if (!curr) {
        return "Currency is required";
      }

      const userId = user?.id;
      if (!userId) return "User not found";

      if (user?.user_metadata?.Preferences?.currency === curr) {
        return "Already set to this currency";
      }

      const { error } = await SetDefaultCurrency(curr);
      if (error) {
        console.log(error);
        return "Failed to update currency";
      }

      updateUser();

      return null;
    }, null);

  const [contractTypeError, contractTypeSubmitAction, contractTypeIsPending] =
    useActionState(async (_previousState: any, formData: FormData) => {
      const cType = formData.get("contractType")?.valueOf() as ContractType;

      if (!cType) {
        return "Type is required";
      }

      const userId = user?.id;
      if (!userId) return "User not found";

      if (user?.user_metadata?.Preferences?.currency === cType) {
        return "Already set to this currency";
      }

      const { error } = await SetDefaultContractType(cType);
      if (error) {
        console.log(error);
        return "Failed to update contract type";
      }

      updateUser();

      return null;
    }, null);

  return (
    <>
      <form className="flex flex-col gap-4" action={currencySubmitAction}>
        <Card>
          <CardHeader>
            <CardTitle>Currency</CardTitle>
            <CardDescription>
              The default currency used for your contracts, you can still change
              this individually
            </CardDescription>
          </CardHeader>
          <CardContent>
            {currencyError && currencyError}
            {!user ? (
              <Skeleton className="w-full h-10" />
            ) : (
              <Combobox
                entity="Currency"
                values={[
                  { label: "GBP £", value: "GBP" },
                  { label: "USD $", value: "USD" },
                  { label: "EUR €", value: "EUR" },
                  { label: "CAD $", value: "CAD" },
                ]}
                className="w-full"
                value={currency}
                onChange={(val) => setCurrency(val as Currency)}
              />
            )}
            <input type="hidden" name="currency" value={currency} />
          </CardContent>
          <CardFooter className="border-t px-6 py-4">
            <Button disabled={currencyIsPending} type="submit">
              Save
            </Button>
          </CardFooter>
        </Card>
      </form>
      <form className="flex flex-col gap-4" action={contractTypeSubmitAction}>
        <Card>
          <CardHeader>
            <CardTitle>Default Type</CardTitle>
            <CardDescription>
              The default type used for your contracts, you can still change
              this when you create a new contract
            </CardDescription>
          </CardHeader>
          <CardContent>
            {contractTypeError && contractTypeError}
            {!user ? (
              <Skeleton className="w-full h-10" />
            ) : (
              <Combobox
                entity="Type"
                values={[
                  { label: "per day", value: "day" },
                  { label: "per hour", value: "hour" },
                ]}
                className="w-full"
                value={contractType}
                onChange={(val) => setContractType(val as ContractType)}
              />
            )}
            <input type="hidden" name="contractType" value={contractType} />
          </CardContent>
          <CardFooter className="border-t px-6 py-4">
            <Button disabled={contractTypeIsPending} type="submit">
              Save
            </Button>
          </CardFooter>
        </Card>
      </form>
    </>
  );
}
